import { decodeName } from './decodeName';
import { purposeMap } from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import type { ClusterSeoMetrics, HierarchicalNode } from '../types';

type IntentMeta = {
  label: string;
  short: string;
  tone: string;
};

const INTENT_MAP: Record<string, IntentMeta> = {
  I: { label: 'اطلاعاتی', short: 'I', tone: 'info' },
  C: { label: 'تجاری', short: 'C', tone: 'warning' },
  T: { label: 'تراکنشی', short: 'T', tone: 'success' },
  N: { label: 'ناوبری', short: 'N', tone: 'secondary' }
};

/** Normalize purpose codes that share a project label. */
const PURPOSE_ALIASES: Record<string, string> = {
  Blog: 'Article',
  Guide: 'Article',
  'Service Page': 'Service',
  'Brand Page': 'Brand'
};

export type DifficultyLevel = {
  id: string;
  min: number;
  max: number;
  label: string;
  badge: string;
};

/** Word count of a keyword phrase (spaces). */
export function phraseWordCount(nameOrLabel: unknown): number {
  const label = decodeName(nameOrLabel);
  const count = String(label).trim().split(/\s+/).filter(Boolean).length;
  return count || 0;
}

export function formatLen(len: number | null | undefined): string {
  if (len == null || Number.isNaN(Number(len))) return 'نامشخص';
  const n = Number(len);
  return n === 1 ? '۱ کلمه' : `${n} کلمه`;
}

/** User-defined keyword difficulty ranges (0-100). */
export const DIFFICULTY_LEVELS: DifficultyLevel[] = [
  { id: 'easy', min: 0, max: 14, label: 'آسان', badge: 'success' },
  { id: 'fairly_easy', min: 15, max: 29, label: 'نسبتا آسان', badge: 'success' },
  { id: 'possible', min: 30, max: 49, label: 'امکان پذیر', badge: 'info' },
  { id: 'hard', min: 50, max: 69, label: 'سخت', badge: 'warning' },
  { id: 'very_hard', min: 70, max: 84, label: 'بسیار سخت', badge: 'danger' },
  { id: 'impossible', min: 85, max: 100, label: 'تقریباً ناممکن', badge: 'dark' }
];

export const DIFFICULTY_ORDER = DIFFICULTY_LEVELS.map(level => level.id);

export function formatVolume(value: number | null | undefined): string {
  const n = Number(value) || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n));
}

export function getDifficultyLevel(
  difficulty: number | null | undefined
): DifficultyLevel | null {
  if (difficulty == null || Number.isNaN(Number(difficulty))) return null;
  const d = Math.min(100, Math.max(0, Number(difficulty)));
  return (
    DIFFICULTY_LEVELS.find(level => d >= level.min && d <= level.max) ||
    DIFFICULTY_LEVELS[DIFFICULTY_LEVELS.length - 1]
  );
}

/** Difficulty level id (easy, hard, …) for progress/charts. */
export function difficultyTone(difficulty: number | null | undefined): string {
  return getDifficultyLevel(difficulty)?.id || 'unknown';
}

export function difficultyLabel(difficulty: number | null | undefined): string {
  return getDifficultyLevel(difficulty)?.label || 'نامشخص';
}

export function difficultyBadge(difficulty: number | null | undefined): string {
  return getDifficultyLevel(difficulty)?.badge || 'secondary';
}

export function mapIntent(code: string): IntentMeta {
  return INTENT_MAP[code] || { label: code, short: code, tone: 'secondary' };
}

export function mapPurpose(purpose: string | null | undefined): string {
  if (purpose == null || purpose === '' || purpose === 'unknown') {
    return 'نامشخص';
  }
  const key = PURPOSE_ALIASES[purpose] || purpose;
  return purposeMap[key] || purposeMap[purpose] || purpose;
}

export function uniqueList(values: Array<string | null | undefined> = []): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))];
}

/** Aggregate SEO metrics from leaf descendants upward. */
export function aggregateSeoFromLeaves(
  leaves: Array<Partial<ClusterSeoMetrics> | null | undefined>
): ClusterSeoMetrics {
  const validLeaves = leaves.filter(Boolean) as Partial<ClusterSeoMetrics>[];

  if (!validLeaves.length) {
    return {
      search_volume_miztols: 0,
      difficulty: null,
      len: null,
      intent: [],
      purpose: [],
      leafCount: 0,
      isAggregated: true
    };
  }

  const volume = validLeaves.reduce(
    (sum, leaf) => sum + (Number(leaf.search_volume_miztols) || 0),
    0
  );

  const diffs = validLeaves
    .map(leaf => Number(leaf.difficulty))
    .filter(d => !Number.isNaN(d));
  const difficulty = diffs.length
    ? Math.round(diffs.reduce((a, b) => a + b, 0) / diffs.length)
    : null;

  const lens = validLeaves
    .map(leaf => Number(leaf.len))
    .filter(n => !Number.isNaN(n) && n > 0);
  const len = lens.length
    ? Math.round(lens.reduce((a, b) => a + b, 0) / lens.length)
    : null;

  const intent = uniqueList(validLeaves.flatMap(leaf => leaf.intent || []));
  const purpose = uniqueList(validLeaves.flatMap(leaf => leaf.purpose || []));

  return {
    search_volume_miztols: volume,
    difficulty,
    len,
    intent,
    purpose,
    leafCount: validLeaves.length,
    isAggregated: true
  };
}

export function normalizeNodeSeo(
  raw: Partial<HierarchicalNode> = {}
): ClusterSeoMetrics {
  const lenFromField =
    raw.len == null || Number.isNaN(Number(raw.len)) ? null : Number(raw.len);
  const len =
    lenFromField ?? (raw.name != null ? phraseWordCount(raw.name) : null);

  return {
    search_volume_miztols: Number(raw.search_volume_miztols) || 0,
    difficulty: raw.difficulty == null ? null : Number(raw.difficulty),
    len,
    intent: Array.isArray(raw.intent) ? raw.intent : [],
    purpose: Array.isArray(raw.purpose) ? raw.purpose : [],
    isAggregated: false
  };
}
